import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel as MuiFormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import api from "../api/client";
import { AuthContext } from "../context/AuthContext";
import { SOUND_PACK_DISCLAIMER, SOUND_PACK_OPTIONS, THEME_OPTIONS } from "../constants/preferences";
import { ColorModeContext } from "../theme/ThemeProvider";
import { SOUND_EVENTS, getSoundCuePath } from "../constants/audio";
import { DIVISION_OPTIONS } from "../constants/regions";

const SOUND_PREVIEW_SEQUENCE = [
  SOUND_EVENTS.INCIDENT_CREATED,
  SOUND_EVENTS.DISPATCH_ASSIGNED,
  SOUND_EVENTS.STATUS_CHECK_REQUIRED,
];

const SOUND_PREVIEW_DELAY_MS = 400;
const CONFIDENTIALITY_DELAY_SECONDS = 60;

// Password validation: at least 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 symbol
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{8,}$/;

const validatePassword = (password) => {
  if (!password) return "Password is required.";
  if (password.length < 8) return "Password must be at least 8 characters.";
  if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter.";
  if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter.";
  if (!/\d/.test(password)) return "Password must contain at least one number.";
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) return "Password must contain at least one symbol.";
  return null;
};

function Onboarding() {
  const navigate = useNavigate();
  const { user, loading, refreshUser, logout } = useContext(AuthContext);
  const colorMode = useContext(ColorModeContext);
  const colorModeRef = useRef(colorMode);
  const hasInitializedForm = useRef(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    rangerId: "",
    division: "",
  });
  const [theme, setTheme] = useState("light");
  const [soundPack, setSoundPack] = useState("male");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const soundPreviewRef = useRef(null);
  const [rangerIdStatus, setRangerIdStatus] = useState({ state: "idle", message: "" });
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showConfidentialityNotice, setShowConfidentialityNotice] = useState(() => {
    return Boolean(user?.requiresConfidentialityAgreement);
  });
  const [acknowledgingNotice, setAcknowledgingNotice] = useState(false);
  const [acknowledgementCountdown, setAcknowledgementCountdown] = useState(CONFIDENTIALITY_DELAY_SECONDS);
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [travelAcknowledged, setTravelAcknowledged] = useState(false);
  const [travelCheckbox, setTravelCheckbox] = useState(false);
  
  // Password change state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    colorModeRef.current = colorMode;
  }, [colorMode]);

  useEffect(() => {
    setShowConfidentialityNotice(Boolean(user?.requiresConfidentialityAgreement));
  }, [user?.requiresConfidentialityAgreement]);

  useEffect(() => {
    if (!showConfidentialityNotice) {
      setAcknowledgementCountdown(CONFIDENTIALITY_DELAY_SECONDS);
      return undefined;
    }

    setAcknowledgementCountdown(CONFIDENTIALITY_DELAY_SECONDS);
    const intervalId = setInterval(() => {
      setAcknowledgementCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [showConfidentialityNotice]);

  const previewTheme = useCallback(
    (value) => {
      if (!value) {
        return;
      }
      colorModeRef.current?.setColorMode?.(value, { persist: false, syncStorage: false });
    },
    []
  );
  const stopSoundPreview = useCallback(() => {
    const previewState = soundPreviewRef.current;
    if (!previewState) {
      return;
    }

    const { audio, timeoutId, onEnded, onError } = previewState;
    if (audio) {
      if (onEnded) {
        audio.removeEventListener("ended", onEnded);
      }
      if (onError) {
        audio.removeEventListener("error", onError);
      }
      audio.pause();
      audio.currentTime = 0;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    soundPreviewRef.current = null;
  }, []);

  const playSoundPreview = useCallback(
    (pack) => {
      stopSoundPreview();

      if (!pack || pack === "no-audio") {
        return;
      }

      const playlist = SOUND_PREVIEW_SEQUENCE.map((eventKey) => getSoundCuePath(pack, eventKey)).filter(Boolean);
      if (!playlist.length) {
        return;
      }

      const playIndex = (idx) => {
        const source = playlist[idx];
        if (!source) {
          soundPreviewRef.current = null;
          return;
        }

        try {
          const audio = new Audio(source);

          function handleNext() {
            const nextIndex = idx + 1;
            if (nextIndex < playlist.length) {
              const timeoutId = setTimeout(() => playIndex(nextIndex), SOUND_PREVIEW_DELAY_MS);
              soundPreviewRef.current = { audio: null, timeoutId, onEnded: null, onError: null };
            } else {
              soundPreviewRef.current = null;
            }
          }

          function cleanupAndNext() {
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("error", handleError);
            handleNext();
          }

          function handleEnded() {
            cleanupAndNext();
          }

          function handleError(err) {
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.warn("Sound preview blocked", err);
            }
            cleanupAndNext();
          }

          audio.addEventListener("ended", handleEnded);
          audio.addEventListener("error", handleError);

          soundPreviewRef.current = { audio, timeoutId: null, onEnded: handleEnded, onError: handleError };

          const playPromise = audio.play();
          if (playPromise?.catch) {
            playPromise.catch((err) => {
              if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn("Sound preview blocked", err);
              }
              handleError(err);
            });
          }
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn("Unable to preview sound pack", err);
          }
        }
      };

      playIndex(0);
    },
    [stopSoundPreview]
  );

  const validatePhone = useCallback((value) => {
    const digitsOnly = value.replace(/\D/g, "");
    if (!digitsOnly) {
      setPhoneError("Phone number is required.");
      return false;
    }
    if (digitsOnly.length !== 10) {
      setPhoneError("Phone number must be exactly 10 digits.");
      return false;
    }
    setPhoneError("");
    return true;
  }, []);

  const validateEmail = useCallback((value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setEmailError("Email is required.");
      return false;
    }
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(trimmed)) {
      setEmailError("Enter a valid email (name@example.com).");
      return false;
    }
    setEmailError("");
    return true;
  }, []);

  useEffect(() => () => {
    stopSoundPreview();
  }, [stopSoundPreview]);

  useEffect(() => {
    if (!user || hasInitializedForm.current) {
      return;
    }

    const allowedDivision = DIVISION_OPTIONS.some((option) => option.value === user.division) ? user.division : "";
    setForm({
      name: user.name || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      rangerId: user.rangerId || "",
      division: allowedDivision,
    });
    const normalizedTheme = (user.preferredTheme || "light").toLowerCase() === "dark" ? "dark" : "light";
    setTheme(normalizedTheme);
    previewTheme(normalizedTheme);
    setSoundPack((user.soundPack || "male").toLowerCase());
    hasInitializedForm.current = true;
  }, [previewTheme, user]);

  useEffect(() => {
    const value = (form.rangerId || "").trim();
    if (!value) {
      setRangerIdStatus({ state: "idle", message: "" });
      return undefined;
    }

    if (value.length !== 3 || !/^[0-9]+$/.test(value)) {
      setRangerIdStatus({ state: "error", message: "Ranger ID must be a 3-digit number." });
      return undefined;
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 100 || numeric >= 999) {
      setRangerIdStatus({ state: "error", message: "Ranger ID must be greater than 100 and less than 999." });
      return undefined;
    }

    let cancelled = false;
    setRangerIdStatus({ state: "checking", message: "Checking availability..." });
    const timer = setTimeout(() => {
      api
        .get("/users/ranger-id/check", { params: { value } })
        .then((response) => {
          if (cancelled) {
            return;
          }
          const available = Boolean(response.data?.available);
          setRangerIdStatus(
            available
              ? { state: "available", message: "Ranger ID available." }
              : { state: "error", message: "Ranger ID is already assigned." }
          );
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          const message = err.response?.data;
          setRangerIdStatus({
            state: "error",
            message: typeof message === "string" ? message : "Unable to validate Ranger ID.",
          });
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.rangerId]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!user.mustChangePassword && !user.requiresOnboarding && !user.requiresConfidentialityAgreement) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, navigate, user]);

  const handlePasswordChange = async () => {
    setPasswordError("");
    
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", { newPassword });
      await refreshUser();
    } catch (err) {
      const message = err.response?.data ?? "Unable to change password.";
      setPasswordError(typeof message === "string" ? message : "Unable to change password.");
    } finally {
      setChangingPassword(false);
    }
  };

  const canSubmit = useMemo(() => {
    return (
      Boolean(form.name.trim()) &&
      rangerIdStatus.state === "available" &&
      Boolean(form.division.trim()) &&
      Boolean(form.phoneNumber.trim()) &&
      Boolean(form.email.trim()) &&
      Boolean(theme) &&
      Boolean(soundPack) &&
      !phoneError &&
      !emailError
    );
  }, [
    emailError,
    form.name,
    form.division,
    form.phoneNumber,
    form.email,
    phoneError,
    soundPack,
    theme,
    rangerIdStatus.state,
  ]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const nextValue = field === "phoneNumber" ? value.replace(/\D/g, "") : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
    if (field === "phoneNumber") {
      validatePhone(nextValue);
    } else if (field === "email") {
      validateEmail(value);
    } else if (field === "division" && value === "Travel Division" && !travelAcknowledged) {
      setShowTravelModal(true);
    }
  };

  const handleTravelModalClose = () => {
    setShowTravelModal(false);
    setTravelCheckbox(false);
    // Reset division if they cancel without acknowledging
    if (!travelAcknowledged) {
      setForm((prev) => ({ ...prev, division: "" }));
    }
  };

  const handleTravelAcknowledge = () => {
    setTravelAcknowledged(true);
    setShowTravelModal(false);
    setTravelCheckbox(false);
  };

  const handleThemeSelect = (_, value) => {
    if (!value) {
      return;
    }
    setTheme(value);
    previewTheme(value);
  };

  const handleSoundPackChange = (event) => {
    const value = event.target.value;
    setSoundPack(value);
    playSoundPreview(value);
  };

  const handleAgreementAcknowledge = async () => {
    if (acknowledgingNotice) {
      return;
    }
    setAcknowledgingNotice(true);
    try {
      await api.post("/users/confidentiality");
      await refreshUser();
      setShowConfidentialityNotice(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const message = err?.response?.data ?? "Unable to refresh your account status.";
      setError(typeof message === "string" ? message : "Unable to refresh your account status.");
    } finally {
      setAcknowledgingNotice(false);
    }
  };

  const handleAgreementDecline = () => {
    setShowConfidentialityNotice(false);
    logout();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    setSaving(true);
    setError("");

    try {
      await api.post("/users/onboarding", {
        name: form.name,
        email: form.email,
        phoneNumber: form.phoneNumber,
        rangerId: form.rangerId,
        division: form.division,
        preferredTheme: theme,
        soundPack,
      });

      setShowConfidentialityNotice(true);
      await refreshUser();
    } catch (err) {
      const message = err.response?.data ?? "Unable to save onboarding details.";
      setError(typeof message === "string" ? message : "Unable to save onboarding details.");
    } finally {
      setSaving(false);
    }
  };

  const rangerIdHelperText = useMemo(() => {
    return rangerIdStatus.message || "Ranger ID must be a three-digit number between 101 and 998.";
  }, [rangerIdStatus.message]);

  const requiresAction = Boolean(
    user?.mustChangePassword || user?.requiresOnboarding || user?.requiresConfidentialityAgreement || showConfidentialityNotice
  );

  if (loading || !user || !requiresAction) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Show password change screen first if required
  if (user.mustChangePassword) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: 6 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: 6 }}>
            <Stack spacing={3}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/rangervault.png`}
                  alt="RangerVault"
                  sx={{ height: 64, mb: 2 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 2 }}>
                  Set Your Password
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  For security, you must set a new password before continuing.
                </Typography>
              </Box>

              {passwordError && (
                <Alert severity="error" onClose={() => setPasswordError("")}>
                  {passwordError}
                </Alert>
              )}

              <Stack spacing={2}>
                <Alert severity="info" icon={false}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Password Requirements:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li>At least 8 characters long</li>
                    <li>At least one uppercase letter (A-Z)</li>
                    <li>At least one lowercase letter (a-z)</li>
                    <li>At least one number (0-9)</li>
                    <li>At least one symbol (!@#$%^&amp;*...)</li>
                  </Box>
                </Alert>

                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  error={confirmPassword && newPassword !== confirmPassword}
                  helperText={confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : ""}
                />

                <MuiFormControlLabel
                  control={
                    <Checkbox
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                    />
                  }
                  label="Show password"
                />
              </Stack>

              <Divider />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" color="inherit" onClick={logout}>
                  Cancel &amp; Logout
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePasswordChange}
                  disabled={changingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                >
                  {changingPassword ? "Saving..." : "Continue"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: 6 }}>

      <Container maxWidth="md">
        {showConfidentialityNotice ? (
          <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: 6 }}>
            <Stack spacing={3}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/rangervault.png`}
                  alt="RangerVault"
                  sx={{ height: 64, mb: 2 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 2 }}>
                  Confidentiality &amp; Non-Disclosure
                </Typography>
                <Typography color="text.secondary">
                  Access to RangerVault requires agreement to the Texas Highway Rangers confidentiality provisions.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" onClose={() => setError("")}>
                  {error}
                </Alert>
              )}

              <Stack spacing={2}>
                <Typography>
                  By proceeding you acknowledge that all operational data, analytics, and communications within RangerVault are
                  confidential operational information owned by the Texas Highway Rangers and its roadside assistance mission.
                </Typography>
                <Typography>
                  You will not share, publish, disclose, store, transmit, or otherwise distribute any portion of the materials below
                  without the explicit written approval of the Texas Highway Rangers Board of Directors:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Screenshots, photographs, videos, or audio captured from RangerVault or affiliated systems.
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Incident details, reports, timelines, maps, or analytical insights derived from RangerVault data.
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Dispatch processes, alerting logic, automation rules, or emergency button procedures.
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Radio frequencies, call signs, telemetry, or encryption parameters used by the Texas Highway Rangers.
                  </Typography>
                  <Typography component="li">
                    Any other operational, technical, or security information obtained through RangerVault, whether or not it is
                    labeled confidential.
                  </Typography>
                </Box>
                <Typography>
                  Distribution to individuals who are not active Texas Highway Rangers members, or to the public (including social
                  media and press), is strictly prohibited unless the Board of Directors grants prior written permission.
                </Typography>
                <Typography>
                  All actions you perform in RangerVault—including viewing incidents, opening video, acknowledging alerts, or
                  triggering emergency workflows—are logged, timestamped, and retained for auditing.
                </Typography>
                <Typography>
                  You understand that violations can result in immediate loss of access, disciplinary action up to termination, civil
                  damages, and potential criminal penalties.
                </Typography>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                <Button color="error" variant="outlined" onClick={handleAgreementDecline} sx={{ order: { xs: 2, sm: 1 } }}>
                  Cancel Account Creation
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAgreementAcknowledge}
                  disabled={acknowledgingNotice || acknowledgementCountdown > 0}
                  sx={{ px: 4, order: { xs: 1, sm: 2 } }}
                >
                  {acknowledgingNotice
                    ? "Please wait..."
                    : acknowledgementCountdown > 0
                      ? `Ready in ${acknowledgementCountdown}s`
                      : "I Agree and Continue"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Paper component="form" onSubmit={handleSubmit} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: 6 }}>
            <Stack spacing={3}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/rangervault.png`}
                  alt="RangerVault"
                  sx={{ height: 64, mb: 2 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 2 }}>
                  Welcome to RangerVault
                </Typography>
                <Typography color="text.secondary">
                  We just need a few details to finish setting up your profile and preferences.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" onClose={() => setError("")}>
                  {error}
                </Alert>
              )}

              <Stack spacing={2}>
                <Typography variant="h6">Profile Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name"
                      value={form.name}
                      onChange={handleChange("name")}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Ranger ID"
                      value={form.rangerId}
                      onChange={handleChange("rangerId")}
                      required
                      fullWidth
                      error={rangerIdStatus.state === "error"}
                      helperText={rangerIdHelperText}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="division-label">Division</InputLabel>
                      <Select
                        labelId="division-label"
                        value={form.division}
                        label="Division"
                        onChange={handleChange("division")}
                      >
                        {DIVISION_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone"
                      value={form.phoneNumber}
                      onChange={handleChange("phoneNumber")}
                      required
                      fullWidth
                      error={Boolean(phoneError)}
                      helperText={phoneError || "Enter a 10-digit phone number."}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                      required
                      fullWidth
                      error={Boolean(emailError)}
                      helperText={emailError || "Enter your work email."}
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <Typography variant="h6">Website Theme</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose the color mode RangerVault should load with by default. You can still switch later from the header.
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={theme}
                  onChange={handleThemeSelect}
                  fullWidth
                  color="primary"
                >
                  {THEME_OPTIONS.map((option) => (
                    <ToggleButton key={option.value} value={option.value} sx={{ p: 2 }}>
                      <Box textAlign="left">
                        <Typography fontWeight={700}>{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <Typography variant="h6">Sound Pack</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select the style of audio cues you want to hear during routine operations.
                </Typography>
                <RadioGroup value={soundPack} onChange={handleSoundPackChange}>
                  {SOUND_PACK_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography fontWeight={600}>{option.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
                <Alert severity="info">{SOUND_PACK_DISCLAIMER}</Alert>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                <Button color="inherit" onClick={logout} sx={{ order: { xs: 2, sm: 1 } }}>
                  Log Out
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!canSubmit || saving}
                  sx={{ px: 4, order: { xs: 1, sm: 2 } }}
                >
                  {saving ? "Saving..." : "Save & Continue"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Container>

      {/* Travel Division Certification Modal */}
      <Dialog
        open={showTravelModal}
        onClose={handleTravelModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
          Travel Division Requirements
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The Travel Division is reserved for Rangers who have completed additional training and certification requirements.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            By selecting this division, you are confirming that you meet the following requirements:
          </Typography>
          <Box sx={{ bgcolor: "action.hover", p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Successfully completed all required Highway Ranger training modules
            </Typography>
            <Typography variant="body2">
              • Obtained the <strong>National Highway Ranger Certification</strong>
            </Typography>
          </Box>
          <MuiFormControlLabel
            control={
              <Checkbox
                checked={travelCheckbox}
                onChange={(e) => setTravelCheckbox(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I acknowledge that I have successfully completed the required training and have obtained the National Highway Ranger certification.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleTravelModalClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleTravelAcknowledge}
            variant="contained"
            disabled={!travelCheckbox}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Onboarding;
